import React, { useState, useEffect } from 'react';
import { StreamSource } from '../constants/channels';
import { GameFilter } from '../types';
import type { TimeWindow } from '../utils/streamMetrics';
import type { SwitchMode } from '../utils/autoSwitch';
import { notificationPermission, requestNotificationPermission, PermissionState } from '../utils/notify';
import { IconSliders } from './icons';
import './Settings.css';

/** Server caps shared lists at 50 — mirror it locally */
export const MAX_CUSTOM_STREAMS = 50;

export type { TimeWindow, SwitchMode };
/** The auto-switch source is the active channel (see constants/channels) */
export type { StreamSource };
export type AutoSwitchBehavior = 'auto' | 'popup';

export interface SpamFilterSettings {
  filterVotes: boolean;    // 1, 2, 3...
  filterCommands: boolean; // !play, !join...
}

interface SettingsProps {
  timeWindow: TimeWindow;
  switchMode: SwitchMode;
  threshold: number;
  languageFilter: string;
  cooldown: number;
  minChatRate: number;
  gameBlacklist: string[];
  streamBlacklist: string[];
  favoriteStream: string;
  /** Owned by the sidebar's channel dial — read here to show the custom list editor */
  streamSource: StreamSource;
  customStreams: string[];
  autoSwitchBehavior: AutoSwitchBehavior;
  spamFilter: SpamFilterSettings;
  /** Owned by the sidebar picker — passed through so it persists with the rest */
  gameFilter: GameFilter | null;
  /** Browser notification when a spike hits while the tab is hidden */
  notifyWhenHidden: boolean;
  isLoggedIn: boolean;
  sessionId: string | null;
  onTimeWindowChange: (window: TimeWindow) => void;
  onSwitchModeChange: (mode: SwitchMode) => void;
  onThresholdChange: (threshold: number) => void;
  onLanguageFilterChange: (lang: string) => void;
  onCooldownChange: (cooldown: number) => void;
  onMinChatRateChange: (rate: number) => void;
  onGameBlacklistChange: (games: string[]) => void;
  onStreamBlacklistChange: (streams: string[]) => void;
  onFavoriteStreamChange: (stream: string) => void;
  onCustomStreamsChange: (streams: string[]) => void;
  onAutoSwitchBehaviorChange: (behavior: AutoSwitchBehavior) => void;
  onSpamFilterChange: (settings: SpamFilterSettings) => void;
  onNotifyWhenHiddenChange: (on: boolean) => void;
}

// Local storage utilities
const SETTINGS_KEY = 'twitch-chat-viewer-settings';

export const saveSettings = (settings: {
  timeWindow: TimeWindow;
  switchMode: SwitchMode;
  threshold: number;
  languageFilter: string;
  cooldown: number;
  minChatRate: number;
  gameBlacklist: string[];
  streamBlacklist: string[];
  favoriteStream: string;
  streamSource: StreamSource;
  customStreams: string[];
  autoSwitchBehavior: AutoSwitchBehavior;
  spamFilter: SpamFilterSettings;
  gameFilter: GameFilter | null;
  notifyWhenHidden: boolean;
}) => {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
};

export const loadSettings = () => {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const Settings: React.FC<SettingsProps> = ({
  timeWindow,
  switchMode,
  threshold,
  languageFilter,
  cooldown,
  minChatRate,
  gameBlacklist,
  streamBlacklist,
  favoriteStream,
  streamSource,
  customStreams,
  autoSwitchBehavior,
  spamFilter,
  gameFilter,
  notifyWhenHidden,
  isLoggedIn,
  sessionId,
  onTimeWindowChange,
  onSwitchModeChange,
  onThresholdChange,
  onLanguageFilterChange,
  onCooldownChange,
  onMinChatRateChange,
  onGameBlacklistChange,
  onStreamBlacklistChange,
  onFavoriteStreamChange,
  onCustomStreamsChange,
  onAutoSwitchBehaviorChange,
  onSpamFilterChange,
  onNotifyWhenHiddenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [alertPermission, setAlertPermission] = useState<PermissionState>(() => notificationPermission());

  // Turning alerts on asks the browser first; the switch only lands if it says yes
  const toggleAlerts = async () => {
    if (notifyWhenHidden) {
      onNotifyWhenHiddenChange(false);
      return;
    }
    const permission = await requestNotificationPermission();
    setAlertPermission(permission);
    onNotifyWhenHiddenChange(permission === 'granted');
  };
  const [gameInput, setGameInput] = useState('');
  const [streamInput, setStreamInput] = useState('');
  const [customStreamInput, setCustomStreamInput] = useState('');
  const [shareHandle, setShareHandle] = useState('');
  const [savedHandle, setSavedHandle] = useState<string | null>(null);
  const [shareStatus, setShareStatus] = useState<string | null>(null);

  // Load user's saved list handle on mount
  useEffect(() => {
    if (sessionId) {
      fetch(`${API_BASE}/api/my-list?sessionId=${sessionId}`)
        .then(res => res.json())
        .then(data => {
          if (data.handle) {
            setSavedHandle(data.handle);
            setShareHandle(data.handle);
          }
        })
        .catch(() => {});
    }
  }, [sessionId]);

  const saveShareableList = async () => {
    if (!sessionId || !shareHandle.trim() || customStreams.length === 0) return;

    setShareStatus('Saving...');
    try {
      const res = await fetch(`${API_BASE}/api/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          handle: shareHandle.trim(),
          streams: customStreams
        })
      });
      const data = await res.json();
      if (data.success) {
        setSavedHandle(data.handle);
        setShareStatus('Saved!');
        setTimeout(() => setShareStatus(null), 2000);
      } else {
        setShareStatus(data.error || 'Failed to save');
        setTimeout(() => setShareStatus(null), 3000);
      }
    } catch {
      setShareStatus('Failed to save');
      setTimeout(() => setShareStatus(null), 3000);
    }
  };

  // Save settings whenever they change
  useEffect(() => {
    saveSettings({
      timeWindow,
      switchMode,
      threshold,
      languageFilter,
      cooldown,
      minChatRate,
      gameBlacklist,
      streamBlacklist,
      favoriteStream,
      streamSource,
      customStreams,
      autoSwitchBehavior,
      spamFilter,
      gameFilter,
      notifyWhenHidden,
    });
  }, [timeWindow, switchMode, threshold, languageFilter, cooldown, minChatRate, gameBlacklist, streamBlacklist, favoriteStream, streamSource, customStreams, autoSwitchBehavior, spamFilter, gameFilter, notifyWhenHidden]);

  // Accepts a bare login or a pasted twitch.tv URL
  const handleAddCustomStream = () => {
    const login = customStreamInput.trim().toLowerCase()
      .replace(/^(?:https?:\/\/)?(?:www\.)?twitch\.tv\//, '')
      .replace(/[^a-z0-9_]/g, '');
    if (!login || customStreams.includes(login) || customStreams.length >= MAX_CUSTOM_STREAMS) return;
    onCustomStreamsChange([...customStreams, login]);
    setCustomStreamInput('');
  };

  const handleAddGame = () => {
    if (gameInput.trim() && !gameBlacklist.includes(gameInput.trim())) {
      onGameBlacklistChange([...gameBlacklist, gameInput.trim()]);
      setGameInput('');
    }
  };

  const handleRemoveGame = (game: string) => {
    onGameBlacklistChange(gameBlacklist.filter(g => g !== game));
  };

  const handleAddStream = () => {
    if (streamInput.trim() && !streamBlacklist.includes(streamInput.trim().toLowerCase())) {
      onStreamBlacklistChange([...streamBlacklist, streamInput.trim().toLowerCase()]);
      setStreamInput('');
    }
  };

  const handleRemoveStream = (stream: string) => {
    onStreamBlacklistChange(streamBlacklist.filter(s => s !== stream));
  };

  return (
    <div className="settings-wrap">
      <button className="settings-toggle" onClick={() => setIsOpen(!isOpen)}>
        <IconSliders size={14} />
        <span className="settings-toggle-label">Settings</span>
      </button>
      <div className={`settings-panel ${isOpen ? 'open' : ''}`}>
      <div className="settings-section">
        <label className="settings-label">Auto-Switch</label>
        <div className="settings-options">
          <button
            className={`settings-btn ${switchMode === 'highest' ? 'active' : ''}`}
            onClick={() => onSwitchModeChange('highest')}
          >
            Highest
          </button>
          <button
            className={`settings-btn ${switchMode === 'threshold' ? 'active' : ''}`}
            onClick={() => onSwitchModeChange('threshold')}
          >
            Threshold
          </button>
          <button
            className={`settings-btn ${switchMode === 'random' ? 'active' : ''}`}
            onClick={() => onSwitchModeChange('random')}
          >
            Random
          </button>
        </div>
      </div>

      <div className="settings-section">
        <label className="settings-label">Switch Behavior</label>
        <div className="settings-options">
          <button
            className={`settings-btn ${autoSwitchBehavior === 'auto' ? 'active' : ''}`}
            onClick={() => onAutoSwitchBehaviorChange('auto')}
          >
            Auto
          </button>
          <button
            className={`settings-btn ${autoSwitchBehavior === 'popup' ? 'active' : ''}`}
            onClick={() => onAutoSwitchBehaviorChange('popup')}
          >
            Popup (S)
          </button>
        </div>
      </div>

      {streamSource === 'custom' && (
        <div className="settings-section">
          <label className="settings-label">Custom Streams</label>
          <div className="settings-input-row">
            <input
              type="text"
              value={customStreamInput}
              onChange={(e) => setCustomStreamInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddCustomStream()}
              placeholder="Add streamer or twitch.tv link"
              className="settings-input"
            />
            <button onClick={handleAddCustomStream} className="settings-btn">
              Add
            </button>
          </div>
          <div className="settings-tags">
            {customStreams.map(stream => (
              <span key={stream} className="game-tag">
                {stream}
                <button onClick={() => onCustomStreamsChange(customStreams.filter(s => s !== stream))} className="remove-tag">×</button>
              </span>
            ))}
          </div>

          {/* Shareable list section */}
          {isLoggedIn && customStreams.length > 0 && (
            <div className="share-box">
              <label className="settings-label share-box-label">
                Share this list
              </label>
              <div className="share-row">
                <span className="share-prefix">pogchamp.tv/@</span>
                <input
                  type="text"
                  value={shareHandle}
                  onChange={(e) => setShareHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="yourname"
                  className="settings-input"
                  maxLength={20}
                />
                <button onClick={saveShareableList} className="settings-btn" disabled={!shareHandle.trim()}>
                  {savedHandle ? 'Update' : 'Save'}
                </button>
              </div>
              {shareStatus && (
                <div className={`share-status ${shareStatus === 'Saved!' ? 'ok' : 'err'}`}>
                  {shareStatus}
                </div>
              )}
              {savedHandle && !shareStatus && (
                <div className="share-live">
                  Live at <a href={`/@${savedHandle}`}>pogchamp.tv/@{savedHandle}</a>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {switchMode === 'threshold' && (
        <div className="settings-section">
          <label className="settings-label">
            Threshold {threshold}x
          </label>
          <input
            type="range"
            min="1.2"
            max="10"
            step="0.1"
            value={threshold}
            onChange={(e) => onThresholdChange(parseFloat(e.target.value))}
            className="settings-slider"
          />
        </div>
      )}

      <div className="settings-section">
        <label className="settings-label">Language</label>
        <select
          value={languageFilter}
          onChange={(e) => onLanguageFilterChange(e.target.value)}
          className="settings-select"
        >
          <option value="all">All</option>
          <option value="en">English</option>
          <option value="es">Español</option>
          <option value="pt">Português</option>
          <option value="fr">Français</option>
          <option value="de">Deutsch</option>
          <option value="it">Italiano</option>
          <option value="ru">Русский</option>
          <option value="ja">日本語</option>
          <option value="ko">한국어</option>
          <option value="zh">中文</option>
        </select>
      </div>

      <div className="settings-section">
        <label className="settings-label">
          Cooldown: {cooldown}s
        </label>
        <input
          type="range"
          min="5"
          max="60"
          step="5"
          value={cooldown}
          onChange={(e) => onCooldownChange(parseInt(e.target.value))}
          className="settings-slider"
        />
      </div>

      <div className="settings-section">
        <label className="settings-label">
          Min Chat Rate: {minChatRate.toFixed(1)}/s
        </label>
        <input
          type="range"
          min="0"
          max="50"
          step="0.5"
          value={minChatRate}
          onChange={(e) => onMinChatRateChange(parseFloat(e.target.value))}
          className="settings-slider"
        />
      </div>

      <div className="settings-section">
        <label className="settings-label">Alerts</label>
        <div className="settings-options">
          <button
            className={`settings-btn ${notifyWhenHidden ? 'active' : ''}`}
            onClick={toggleAlerts}
            disabled={alertPermission === 'unsupported'}
            title="Browser notification when a spike hits while you're on another tab"
          >
            When tab is hidden
          </button>
        </div>
        <div className="settings-hint">
          {alertPermission === 'unsupported'
            ? 'This browser has no notifications.'
            : alertPermission === 'denied'
              ? 'Notifications are blocked for this site — allow them in the browser to use alerts.'
              : 'A browser notification when a spike hits while you are on another tab.'}
        </div>
      </div>

      <div className="settings-section">
        <label className="settings-label">Spam Filter</label>
        <div className="settings-options">
          <button
            className={`settings-btn ${spamFilter.filterVotes ? 'active' : ''}`}
            onClick={() => onSpamFilterChange({ ...spamFilter, filterVotes: !spamFilter.filterVotes })}
          >
            1/2/3
          </button>
          <button
            className={`settings-btn ${spamFilter.filterCommands ? 'active' : ''}`}
            onClick={() => onSpamFilterChange({ ...spamFilter, filterCommands: !spamFilter.filterCommands })}
          >
            !cmds
          </button>
        </div>
      </div>

      <div className="settings-section">
        <label className="settings-label">Game Blacklist</label>
        <div className="settings-input-row">
          <input
            type="text"
            value={gameInput}
            onChange={(e) => setGameInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddGame()}
            placeholder="Add game to blacklist"
            className="settings-input"
          />
          <button onClick={handleAddGame} className="settings-btn">Add</button>
        </div>
        <div className="settings-tags">
          {gameBlacklist.map(game => (
            <span key={game} className="game-tag">
              {game}
              <button onClick={() => handleRemoveGame(game)} className="remove-tag">×</button>
            </span>
          ))}
        </div>
      </div>

      <div className="settings-section">
        <label className="settings-label">Stream Blacklist</label>
        <div className="settings-input-row">
          <input
            type="text"
            value={streamInput}
            onChange={(e) => setStreamInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddStream()}
            placeholder="Add username to blacklist"
            className="settings-input"
          />
          <button onClick={handleAddStream} className="settings-btn">Add</button>
        </div>
        <div className="settings-tags">
          {streamBlacklist.map(stream => (
            <span key={stream} className="game-tag">
              {stream}
              <button onClick={() => handleRemoveStream(stream)} className="remove-tag">×</button>
            </span>
          ))}
        </div>
      </div>

      <div className="settings-section">
        <label className="settings-label">Favorite Stream</label>
        <input
          type="text"
          value={favoriteStream}
          onChange={(e) => onFavoriteStreamChange(e.target.value.toLowerCase())}
          placeholder="Username (auto-return)"
          className="settings-input"
        />
      </div>
    </div>
    </div>
  );
};
